import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import StatusPill from '../../components/ui/StatusPill';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/overlays/Modal';
import { Field, Select, TextInput } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { listOrderedReports } from '../../actions/listActions';
import { createReport, getReportTypes } from '../../actions/orderReportAction';
import { formatDateTime } from '../../helpers/format';
import { statusLabel } from '../../design/tokens';
import './operations.css';

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  full_name: '',
  email: '',
  gender: '',
  date_of_birth: '',
  time_of_birth: '',
  report_type: '',
};

export default function OrderReport() {
  const dispatch = useDispatch();
  const { toast } = useOverlays();
  const activeUser = useSelector((s) => s.auth?.active_user?.data);
  const [ordering, setOrdering] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [reportTypes, setReportTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const list = useListState({
    views: [],
    fetcher: listOrderedReports,
    initialSort: { column: 'created_at', direction: 'desc' },
  });

  useEffect(() => {
    getReportTypes(dispatch)
      .then((types) =>
        setReportTypes(
          (types || []).map((t) => ({ value: t.report_type, label: t.report_name || t.report_type }))
        )
      )
      .catch(() => setReportTypes([]));
  }, [dispatch]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const valid =
    form.full_name.trim() && form.email.trim() && form.gender && form.date_of_birth && form.report_type;

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      // The API takes a single combined birth datetime; noon is the convention when
      // the birth time is unknown.
      const time = form.time_of_birth || '12:00';
      await createReport(dispatch, {
        report_type: form.report_type,
        full_name: form.full_name,
        email: form.email || activeUser?.email,
        gender: form.gender,
        date_of_birth: `${form.date_of_birth} ${time}`,
      });
      toast('Report ordered', { tone: 'success' });
      setOrdering(false);
      setForm(EMPTY_FORM);
      list.reload();
    } catch {
      toast('Could not order the report', { tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, form, activeUser, toast, list]);

  const columns = useMemo(
    () => [
      { key: 'client', header: 'Client', width: 230, sortable: true, render: (r) => r.client_name || '—' },
      { key: 'gender', header: 'Gender', width: 110, muted: true, render: (r) => r.gender || '—' },
      {
        key: 'report_type',
        header: 'Report type',
        width: 220,
        sortable: true,
        render: (r) => statusLabel(r.report_type),
      },
      {
        key: 'status',
        header: 'Payment status',
        width: 160,
        sortable: true,
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: 'created_at',
        header: 'Created at',
        width: 180,
        muted: true,
        sortable: true,
        render: (r) => formatDateTime(r.created_at),
      },
      {
        key: 'pdf',
        header: '',
        width: 60,
        render: (r) =>
          r.report_url ? (
            <IconButton
              icon="bx-download"
              size="sm"
              label="Open report"
              onClick={(e) => {
                e.stopPropagation();
                window.open(r.report_url, '_blank', 'noopener');
              }}
            />
          ) : null,
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        section="Operations"
        title="Order report"
        subtitle="Order a report on a client's behalf and track the ones you have placed."
        primaryAction={{ label: 'Order report', icon: 'bx-plus', onClick: () => setOrdering(true) }}
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="orders"
        selectable={false}
        searchPlaceholder="Client or email"
        empty={{
          icon: 'bx-receipt',
          title: 'No orders yet',
          description: 'Reports you order will appear here.',
          actionLabel: 'Order report',
          actionIcon: 'bx-plus',
          onAction: () => setOrdering(true),
        }}
      />

      <Modal
        open={ordering}
        onClose={() => setOrdering(false)}
        title="Order a report"
        size="form"
        confirmLabel="Place order"
        confirmDisabled={!valid}
        confirmLoading={submitting}
        onConfirm={submit}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingTop: 4 }}>
          <Field label="Full name" required className="sv-span-2">
            <TextInput value={form.full_name} onChange={set('full_name')} placeholder="Client's full name" />
          </Field>
          <Field label="Email" required>
            <TextInput type="email" value={form.email} onChange={set('email')} placeholder="client@example.com" />
          </Field>
          <Field label="Gender" required>
            <Select options={GENDERS} placeholder="Select" value={form.gender} onChange={set('gender')} />
          </Field>
          <Field label="Date of birth" required>
            <TextInput type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
          </Field>
          <Field label="Time of birth" hint="Defaults to 12:00 if unknown">
            <TextInput type="time" value={form.time_of_birth} onChange={set('time_of_birth')} />
          </Field>
          <Field label="Report type" required className="sv-span-2">
            <Select
              options={reportTypes}
              placeholder="Select a report"
              value={form.report_type}
              onChange={set('report_type')}
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
